import { COLLABORATOR_CONTRACT_TYPE, type FunctionalRoleCode, type Project } from '@/types';

export const SCREENPLAY_UPLOAD_PERMISSION_DENIED_MESSAGE =
  '暂无操作该项的权限，如果需要请联系组织管理员增加权限。';

export const SCREENPLAY_CREATE_PERMISSION_DENIED_MESSAGE =
  '当前账号暂无创建剧本的权限，如需创建请联系项目管理员调整职能。';

export const STORYBOARD_CREATE_PERMISSION_DENIED_MESSAGE =
  '当前账号暂无导入分镜的权限，如需导入请联系项目管理员调整职能。';

export const SCREENPLAY_CREATE_PANEL_TITLE = '项目权限';
export const SCREENPLAY_CREATE_LABEL = '允许新增单集';
export const CONTRACTOR_SCREENPLAY_CREATE_LABEL = '允许承制方分配新增单集权限';
export const SCREENPLAY_CREATE_TAG_LABEL = '可建单集';
export const SCREENPLAY_CREATE_DESCRIPTION = '新建后可管理该集成员';
export const SCREENPLAY_CREATE_SCOPE_HINT = '此勾选在全部单集同步，不含已有单集';
export const SCREENPLAY_CREATE_RECOGNITION_HINT =
  '允许新增单集。新建后可管理该集成员。勾选在全部单集同步，不含已有单集。';
export const SCREENPLAY_CREATE_TREE_SAVED_MESSAGE =
  '新增单集权限对整棵集树生效。新建单集后可管理该集成员。';
export const EPISODE_PERMISSION_MENU_TITLE = '本集权限（仅当前单集生效）';
export const PARENT_PERMISSION_MENU_TITLE = '项目全局权限（所有单集同步勾选）';
export const MANAGE_ALL_ASSETS_LABEL = '允许管理所有资产';
export const MANAGE_ALL_ASSETS_TAG_LABEL = '可管理资产';
export const MANAGE_ALL_ASSETS_DESCRIPTION = '关闭时只能管理本团队新增的资产，打开后可管理全部资产';
export const MANAGE_ALL_ASSETS_REQUIRED_MESSAGE = '需要开启「允许管理所有资产」后才能操作';
export const CONTRACTOR_ASSIGNABLE_PERMISSION_TITLE = '先给承制通道开通，承制方才能再分给自己的人';
export const CONTRACTOR_ASSIGNABLE_PERMISSION_MENU_TITLE = '承制方再分配';
export const CONTRACTOR_ASSIGNABLE_PERMISSION_DESCRIPTION =
  '下载、管理本集成员和新增单集要先在这里打开。打开后，承制方才能把对应权限分给自己团队的成员。';
export const CONTRACTOR_TEAM_ASSET_PERMISSION_MENU_TITLE = '直接给全团队';
export const CONTRACTOR_TEAM_ASSET_PERMISSION_TITLE = '直接给整个承制团队';
export const CONTRACTOR_TEAM_ASSET_PERMISSION_DESCRIPTION =
  '不需要承制方再分配。打开后，该承制组织都可以管理项目里的全部资产。';
export const CONTRACTOR_SCREENPLAY_CREATE_DESCRIPTION =
  '开通后可分配给成员；成员新建单集后可管理该集成员';

/** 制作承制不提供「允许承制方分配新增单集」的开通入口。 */
export function canOfferContractorScreenplayCreate(
  contractType: number | null | undefined
): boolean {
  return contractType !== COLLABORATOR_CONTRACT_TYPE.PRODUCTION;
}

/**
 * 制作承制后续提交默认 false，避免新开通。
 * 已有授权保持原值，再次邀请或保存其他权限时不把通道关掉。
 */
export function resolveContractorScreenplayCreateForSubmit(input: {
  contractType: number | null | undefined;
  requested: boolean;
  existing?: boolean;
}): boolean {
  if (!canOfferContractorScreenplayCreate(input.contractType)) {
    return Boolean(input.existing);
  }
  return input.requested;
}

export const STORYBOARD_EDIT_PERMISSION_DENIED_MESSAGE =
  '仅查看，暂无分镜编辑权限。如需编辑请联系项目管理员调整职能。';

type ProjectPermissionShape = {
  id?: Project['id'];
  ownerId?: Project['ownerId'];
  memberRole?: Project['memberRole'];
  functionalRoles?: Project['functionalRoles'];
  permissionRestrictionReason?: Project['permissionRestrictionReason'];
  status?: Project['status'] | string | number | null;
  canDownload?: boolean | null;
  canManageMembers?: boolean | null;
  canCreateScreenplays?: boolean | null;
};

export type ProjectPermissionInput = ProjectPermissionShape & {
  type?: Project['type'] | 'project' | 'subproject';
  ownerType?: Project['ownerType'];
  orgId?: string | null;
  parentProjectId?: string | null;
  /**
   * Workspace tree nodes use `type: "project"` for the node kind and carry
   * folderCategory=1 for personal projects, so they need a contextual owner fallback.
   */
  folderCategory?: number | string;
};

export const PROJECT_ACTION_KEYS = [
  'project.editSettings',
  'project.renameEpisode',
  'project.setCover',
  'project.share',
  'project.exportPackage',
  'project.exportReport',
  'project.archive',
  'project.delete',
  'project.manageMembers',
  'project.inviteExternalMembers',
  'screenplay.upload',
  'screenplay.create',
  'screenplay.edit',
  'screenplay.parse',
  'storyboard.create',
  'storyboard.edit',
  'storyboard.delete',
  'storyboard.submitReview',
  'storyboard.approve',
  'asset.approve',
  'production.approveFinal',
  'task.assign',
  'screenplay.polish',
  'storyboard.textAssist',
  'storyboard.dialoguePolish',
  'storyboard.editDialogue',
  'storyboard.linkResource',
  'storyboard.plansCreate',
  'storyboard.plansEdit',
  'storyboard.plansDelete',
  'storyboard.generateImage',
  'storyboard.generateVideo',
  'storyboard.generateVoice',
  'storyboard.generateSoundEffect',
  'canvas.storyboardLayoutEdit',
  'canvas.assetLayoutEdit',
  'canvas.storyboardReferenceManage',
  'canvas.assetReferenceManage',
  'canvas.referenceUpload',
  'resource.createAny',
  'resource.deleteSubmitReview',
  'resource.deleteReviewApprove',
  'resource.promptAssist',
  'resource.generateImage',
  'resource.uploadAny',
  'resource.deleteAny',
  'resource.manageTags',
  'timeline.drag',
  // 细粒度对白动作
  'storyboard.dialogueCreate',
  'storyboard.dialogueEdit',
  'storyboard.dialogueDelete',
  // 细粒度分镜图片/视频动作
  'storyboard.imageUpload',
  'storyboard.imageDownload',
  'storyboard.imageDelete',
  'storyboard.videoUpload',
  'storyboard.videoDownload',
  'storyboard.videoDelete',
  'storyboard.upscaleVideo',
  'storyboard.upscaleImage',
  'storyboard.trimVideo',
  // 细粒度配音动作
  'storyboard.voiceEdit',
  'storyboard.voiceDownload',
  'storyboard.voiceDelete',
  // 细粒度音效动作
  'storyboard.soundEffectEdit',
  'storyboard.soundEffectDownload',
  'storyboard.soundEffectDelete',
  // 细粒度资源库 - 角色
  'resource.charactersCreate',
  'resource.charactersEdit',
  'resource.charactersDelete',
  'resource.charactersUpload',
  'resource.charactersDownload',
  'resource.charactersTagCreate',
  'resource.charactersTagEdit',
  'resource.charactersTagDelete',
  // 细粒度资源库 - 场景
  'resource.scenesCreate',
  'resource.scenesEdit',
  'resource.scenesDelete',
  'resource.scenesUpload',
  'resource.scenesDownload',
  'resource.scenesTagCreate',
  'resource.scenesTagEdit',
  'resource.scenesTagDelete',
  // 细粒度资源库 - 道具
  'resource.propsCreate',
  'resource.propsEdit',
  'resource.propsDelete',
  'resource.propsUpload',
  'resource.propsDownload',
  'resource.propsTagCreate',
  'resource.propsTagEdit',
  'resource.propsTagDelete',
  // 细粒度资源库 - 实拍
  'resource.footageUpload',
  'resource.footageEdit',
  'resource.footageDelete',
] as const;

export type ProjectActionKey = (typeof PROJECT_ACTION_KEYS)[number];

export type ProjectPermissionFacts = {
  project: ProjectPermissionInput | null | undefined;
  currentUserId?: string | null;
  currentUserOrgRole?: number | null;
  orgPermissionCodes?: readonly string[] | null;
  readonlyProjectShareToken?: string | null;
};

export type ProjectCapabilities = {
  canView: boolean;
  canEdit: boolean;
  canManage: boolean;
  canDelete: boolean;
  canShare: boolean;
  canExport: boolean;
  canGenerate: boolean;
};

export type ProjectMenuAccess = {
  canEditSettings: boolean;
  canSetCover: boolean;
  canManageMembers: boolean;
  canShare: boolean;
  canExport: boolean;
  canArchive: boolean;
  canDelete: boolean;
  hasAnyAction: boolean;
};

export type ProjectEditorAccess = {
  readonly facts: ProjectPermissionFacts;
  readonly actions: Readonly<Record<ProjectActionKey, boolean>>;
  readonly capabilities: ProjectCapabilities;
  readonly readOnly: boolean;
  readonly readOnlyReason: string | null;
  readonly canAccessParseFlow: boolean;
  readonly storyboardReviewLock?: {
    readonly statusLabel: string;
    readonly reason: string;
    readonly disabledFeatures: readonly string[];
    readonly autoOpenKey?: string | number | null;
  } | null;
  can: (action: ProjectActionKey) => boolean;
  disabledReason: (action: ProjectActionKey) => string | null;
};

const PROJECT_PRODUCTION_FUNCTIONAL_ROLE_CODES = [
  'director',
  'storyboard_artist',
  'visual_designer',
  'resource_producer',
  'voice_actor',
  'sound_designer',
] as const;

const PROJECT_COVER_ORG_PERMISSION_CODES = ['project.create'] as const;

type ProjectActionPolicy = {
  permissionCodes?: readonly string[];
  functionalRoleCodes?: readonly FunctionalRoleCode[];
  allowOwner?: boolean;
  allowProjectMemberOwner?: boolean;
  allowProjectEditor?: boolean;
  allowOrgAdmin?: boolean;
  disabledReason: string;
  requiresActiveProject?: boolean;
};

const PROJECT_ACTION_POLICY: Record<ProjectActionKey, ProjectActionPolicy> = {
  'project.editSettings': {
    permissionCodes: ['project.edit_any'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑项目设置的权限',
  },
  'project.renameEpisode': {
    permissionCodes: ['project.edit_any'],
    functionalRoleCodes: ['director'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无重命名单集的权限',
  },
  'project.setCover': {
    permissionCodes: [
      'project.create',
      'project.edit_any',
      'resource.characters_upload',
      'resource.scenes_upload',
      'resource.props_upload',
      'resource.footage_upload',
      'resource.characters_edit',
      'resource.scenes_edit',
      'resource.props_edit',
      'resource.footage_edit',
    ],
    functionalRoleCodes: PROJECT_PRODUCTION_FUNCTIONAL_ROLE_CODES,
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无设置项目封面的权限',
  },
  'project.share': {
    permissionCodes: ['project.share'],
    allowOwner: true,
    allowProjectEditor: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无分享项目的权限',
  },
  'project.exportPackage': {
    permissionCodes: ['project.export_package'],
    functionalRoleCodes: ['post_production_artist'],
    allowOwner: true,
    allowProjectMemberOwner: true,
    allowOrgAdmin: true,
    requiresActiveProject: false,
    disabledReason: '当前账号暂无导出项目工程包的权限',
  },
  'project.exportReport': {
    permissionCodes: ['project.export_report'],
    functionalRoleCodes: ['post_production_artist'],
    allowOwner: true,
    allowOrgAdmin: true,
    requiresActiveProject: false,
    disabledReason: '当前账号暂无导出项目运营报表的权限',
  },
  'project.archive': {
    permissionCodes: ['project.edit_any'],
    allowOwner: true,
    allowOrgAdmin: true,
    requiresActiveProject: false,
    disabledReason: '当前账号暂无归档项目的权限',
  },
  'project.delete': {
    permissionCodes: ['project.delete', 'project.delete_any'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除项目的权限',
  },
  'project.manageMembers': {
    permissionCodes: [
      'project.member.invite',
      'project.member.manage',
      'project.member.assign_roles',
      'project.member.remove',
    ],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无管理项目成员的权限',
  },
  'project.inviteExternalMembers': {
    permissionCodes: ['project.member.external_invite'],
    functionalRoleCodes: [
      'director',
      'storyboard_artist',
      'visual_designer',
      'resource_producer',
      'voice_actor',
      'sound_designer',
    ],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无邀请外部成员的权限',
  },
  'screenplay.upload': {
    permissionCodes: ['screenplay.upload'],
    functionalRoleCodes: ['director'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: SCREENPLAY_UPLOAD_PERMISSION_DENIED_MESSAGE,
  },
  'screenplay.create': {
    permissionCodes: ['screenplay.create'],
    functionalRoleCodes: ['director'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: SCREENPLAY_CREATE_PERMISSION_DENIED_MESSAGE,
  },
  'screenplay.edit': {
    permissionCodes: ['screenplay.edit'],
    functionalRoleCodes: ['director'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑剧本的权限',
  },
  'screenplay.parse': {
    permissionCodes: ['screenplay.parse'],
    functionalRoleCodes: ['director', 'storyboard_artist'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无使用 AI 剧本助手的权限',
  },
  'storyboard.create': {
    permissionCodes: ['storyboard.create'],
    functionalRoleCodes: ['director', 'storyboard_artist'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无创建分镜的权限',
  },
  'storyboard.edit': {
    permissionCodes: ['storyboard.edit'],
    functionalRoleCodes: ['director', 'storyboard_artist'],
    allowOwner: true,
    allowProjectMemberOwner: true,
    allowOrgAdmin: true,
    disabledReason: STORYBOARD_EDIT_PERMISSION_DENIED_MESSAGE,
  },
  'storyboard.delete': {
    permissionCodes: ['storyboard.delete'],
    functionalRoleCodes: ['director', 'storyboard_artist'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除分镜的权限',
  },
  'storyboard.submitReview': {
    permissionCodes: ['storyboard.submit_review'],
    functionalRoleCodes: ['director', 'storyboard_artist'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无提交分镜审核的权限',
  },
  'storyboard.approve': {
    permissionCodes: ['storyboard.approve'],
    functionalRoleCodes: ['director'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无审核分镜的权限',
  },
  'asset.approve': {
    permissionCodes: ['asset.approve'],
    functionalRoleCodes: ['director'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无审核项目素材的权限',
  },
  'production.approveFinal': {
    permissionCodes: ['production.approve_final'],
    functionalRoleCodes: ['director'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无终审项目成片的权限',
  },
  'task.assign': {
    permissionCodes: ['task.assign'],
    functionalRoleCodes: ['director'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无分派项目任务的权限',
  },
  'screenplay.polish': {
    permissionCodes: ['screenplay.polish'],
    functionalRoleCodes: ['director'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无剧本润色权限',
  },
  'storyboard.textAssist': {
    permissionCodes: ['storyboard.text_assist'],
    functionalRoleCodes: ['director', 'storyboard_artist'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无分镜文本辅助权限',
  },
  'storyboard.dialoguePolish': {
    permissionCodes: ['storyboard.dialogue_polish'],
    functionalRoleCodes: ['director', 'storyboard_artist', 'voice_actor'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无对白润色权限',
  },
  'storyboard.editDialogue': {
    permissionCodes: ['storyboard.edit', 'storyboard.dialogue_create', 'storyboard.dialogue_edit'],
    functionalRoleCodes: ['director', 'storyboard_artist', 'voice_actor'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无新增或修改对白文字的权限',
  },
  'storyboard.linkResource': {
    permissionCodes: ['storyboard.resource_link'],
    functionalRoleCodes: ['director', 'storyboard_artist', 'resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无关联分镜资源的权限',
  },
  'storyboard.plansCreate': {
    permissionCodes: ['storyboard.plans_create'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无新增分镜方案的权限',
  },
  'storyboard.plansEdit': {
    permissionCodes: ['storyboard.plans_edit'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑分镜方案的权限',
  },
  'storyboard.plansDelete': {
    permissionCodes: ['storyboard.plans_delete'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除分镜方案的权限',
  },
  'storyboard.generateImage': {
    permissionCodes: ['storyboard.image_generate'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无分镜生图权限',
  },
  'storyboard.generateVideo': {
    permissionCodes: ['storyboard.video_generate'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无分镜视频生成权限',
  },
  'storyboard.generateVoice': {
    permissionCodes: ['storyboard.voice_generate'],
    functionalRoleCodes: ['voice_actor'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无分镜配音生成权限',
  },
  'storyboard.generateSoundEffect': {
    permissionCodes: ['storyboard.sound_effect_generate'],
    functionalRoleCodes: ['sound_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无分镜音效生成权限',
  },
  // D13：layout_edit 门控废弃；码保留供自定义权限组，默认角色不再映射。
  'canvas.storyboardLayoutEdit': {
    permissionCodes: ['canvas.storyboard_layout_edit'],
    functionalRoleCodes: [],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无保存分镜画布布局的权限',
  },
  'canvas.assetLayoutEdit': {
    permissionCodes: ['canvas.asset_layout_edit'],
    functionalRoleCodes: [],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无保存素材画布布局的权限',
  },
  'canvas.storyboardReferenceManage': {
    permissionCodes: ['canvas.storyboard_reference_manage'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无管理分镜画布参考图的权限',
  },
  'canvas.assetReferenceManage': {
    permissionCodes: ['canvas.asset_reference_manage'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无管理素材画布参考图的权限',
  },
  // D13：画布上传实拍改挂 footage_upload；本码默认角色映射清空。
  'canvas.referenceUpload': {
    permissionCodes: ['canvas.reference_upload'],
    functionalRoleCodes: [],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无上传画布参考媒体的权限',
  },
  'resource.createAny': {
    permissionCodes: [
      'resource.characters_create',
      'resource.scenes_create',
      'resource.props_create',
      'resource.characters_tag_create',
      'resource.scenes_tag_create',
      'resource.props_tag_create',
    ],
    functionalRoleCodes: ['director', 'storyboard_artist', 'visual_designer', 'resource_producer'],
    allowOwner: false,
    allowOrgAdmin: false,
    disabledReason: '当前账号暂无创建项目素材的权限',
  },
  'resource.deleteSubmitReview': {
    permissionCodes: ['resource.delete_submit_review'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: false,
    allowOrgAdmin: false,
    disabledReason: '当前账号无需提交资源删除审核',
  },
  'resource.deleteReviewApprove': {
    permissionCodes: ['resource.delete_review_approve'],
    functionalRoleCodes: ['director'],
    allowOwner: false,
    allowOrgAdmin: false,
    disabledReason: '当前账号暂无资源删除审核权限',
  },
  'resource.promptAssist': {
    permissionCodes: [
      'resource.characters_prompt_assist',
      'resource.scenes_prompt_assist',
      'resource.props_prompt_assist',
    ],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无资源提示词辅助权限',
  },
  'resource.generateImage': {
    permissionCodes: [
      'resource.characters_generate_image',
      'resource.scenes_generate_image',
      'resource.props_generate_image',
    ],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无资源生图权限',
  },
  'resource.uploadAny': {
    permissionCodes: [
      'resource.characters_upload',
      'resource.scenes_upload',
      'resource.props_upload',
      'resource.footage_upload',
    ],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无上传项目素材的权限',
  },
  'resource.deleteAny': {
    permissionCodes: [
      'resource.characters_delete',
      'resource.scenes_delete',
      'resource.props_delete',
      'resource.footage_delete',
    ],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除项目素材的权限',
  },
  'resource.manageTags': {
    permissionCodes: [
      'resource.characters_tag_create',
      'resource.characters_tag_edit',
      'resource.characters_tag_delete',
      'resource.scenes_tag_create',
      'resource.scenes_tag_edit',
      'resource.scenes_tag_delete',
      'resource.props_tag_create',
      'resource.props_tag_edit',
      'resource.props_tag_delete',
    ],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无管理项目素材标签的权限',
  },
  'timeline.drag': {
    permissionCodes: ['storyboard.edit'],
    functionalRoleCodes: ['director', 'storyboard_artist'],
    allowOwner: true,
    allowProjectMemberOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无调整时间轴的权限',
  },
  'storyboard.dialogueCreate': {
    permissionCodes: ['storyboard.dialogue_create'],
    functionalRoleCodes: ['director', 'storyboard_artist', 'voice_actor'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无新增对白的权限',
  },
  'storyboard.dialogueEdit': {
    permissionCodes: ['storyboard.dialogue_edit'],
    functionalRoleCodes: ['director', 'storyboard_artist', 'voice_actor'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑对白的权限',
  },
  'storyboard.dialogueDelete': {
    permissionCodes: ['storyboard.dialogue_delete'],
    functionalRoleCodes: ['director', 'storyboard_artist', 'voice_actor'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除对白的权限',
  },
  'storyboard.imageUpload': {
    permissionCodes: ['storyboard.image_upload'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无上传分镜图片的权限',
  },
  'storyboard.imageDownload': {
    permissionCodes: ['storyboard.image_download'],
    allowOwner: false,
    allowOrgAdmin: false,
    disabledReason: '当前账号暂无下载分镜图片的权限',
  },
  'storyboard.imageDelete': {
    permissionCodes: ['storyboard.image_delete'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除分镜图片的权限',
  },
  'storyboard.videoUpload': {
    permissionCodes: ['storyboard.video_upload'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无上传分镜视频的权限',
  },
  'storyboard.videoDownload': {
    permissionCodes: ['storyboard.video_download'],
    allowOwner: false,
    allowOrgAdmin: false,
    disabledReason: '当前账号暂无下载分镜视频的权限',
  },
  'storyboard.videoDelete': {
    permissionCodes: ['storyboard.video_delete'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除分镜视频的权限',
  },
  'storyboard.upscaleVideo': {
    permissionCodes: ['storyboard.video_upscale'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无分镜视频高清优化权限',
  },
  'storyboard.upscaleImage': {
    permissionCodes: ['storyboard.image_upscale'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无分镜图片高清优化权限',
  },
  'storyboard.trimVideo': {
    permissionCodes: ['storyboard.video_trim'],
    functionalRoleCodes: ['resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无裁剪分镜视频权限',
  },
  'storyboard.voiceEdit': {
    permissionCodes: ['storyboard.voice_edit'],
    functionalRoleCodes: ['voice_actor'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑配音版本的权限',
  },
  'storyboard.voiceDownload': {
    permissionCodes: ['storyboard.voice_download'],
    allowOwner: false,
    allowOrgAdmin: false,
    disabledReason: '当前账号暂无下载配音的权限',
  },
  'storyboard.voiceDelete': {
    permissionCodes: ['storyboard.voice_delete'],
    functionalRoleCodes: ['voice_actor'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除配音的权限',
  },
  'storyboard.soundEffectEdit': {
    permissionCodes: ['storyboard.sound_effect_edit'],
    functionalRoleCodes: ['sound_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑音效的权限',
  },
  'storyboard.soundEffectDownload': {
    permissionCodes: ['storyboard.sound_effect_download'],
    allowOwner: false,
    allowOrgAdmin: false,
    disabledReason: '当前账号暂无下载音效的权限',
  },
  'storyboard.soundEffectDelete': {
    permissionCodes: ['storyboard.sound_effect_delete'],
    functionalRoleCodes: ['sound_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除音效的权限',
  },
  'resource.charactersCreate': {
    permissionCodes: ['resource.characters_create'],
    functionalRoleCodes: ['director', 'storyboard_artist', 'visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无创建角色的权限',
  },
  'resource.charactersEdit': {
    permissionCodes: ['resource.characters_edit'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑角色的权限',
  },
  'resource.charactersDelete': {
    permissionCodes: ['resource.characters_delete'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除角色的权限',
  },
  'resource.charactersUpload': {
    permissionCodes: ['resource.characters_upload'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无上传角色素材的权限',
  },
  'resource.charactersDownload': {
    permissionCodes: ['resource.characters_download'],
    allowOwner: false,
    allowOrgAdmin: false,
    disabledReason: '当前账号暂无下载角色素材的权限',
  },
  'resource.charactersTagCreate': {
    permissionCodes: ['resource.characters_tag_create'],
    functionalRoleCodes: ['visual_designer', 'resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无创建角色标签的权限',
  },
  'resource.charactersTagEdit': {
    permissionCodes: ['resource.characters_tag_edit'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑角色标签的权限',
  },
  'resource.charactersTagDelete': {
    permissionCodes: ['resource.characters_tag_delete'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除角色标签的权限',
  },
  'resource.scenesCreate': {
    permissionCodes: ['resource.scenes_create'],
    functionalRoleCodes: ['director', 'storyboard_artist', 'visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无创建场景的权限',
  },
  'resource.scenesEdit': {
    permissionCodes: ['resource.scenes_edit'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑场景的权限',
  },
  'resource.scenesDelete': {
    permissionCodes: ['resource.scenes_delete'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除场景的权限',
  },
  'resource.scenesUpload': {
    permissionCodes: ['resource.scenes_upload'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无上传场景素材的权限',
  },
  'resource.scenesDownload': {
    permissionCodes: ['resource.scenes_download'],
    allowOwner: false,
    allowOrgAdmin: false,
    disabledReason: '当前账号暂无下载场景素材的权限',
  },
  'resource.scenesTagCreate': {
    permissionCodes: ['resource.scenes_tag_create'],
    functionalRoleCodes: ['visual_designer', 'resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无创建场景标签的权限',
  },
  'resource.scenesTagEdit': {
    permissionCodes: ['resource.scenes_tag_edit'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑场景标签的权限',
  },
  'resource.scenesTagDelete': {
    permissionCodes: ['resource.scenes_tag_delete'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除场景标签的权限',
  },
  'resource.propsCreate': {
    permissionCodes: ['resource.props_create'],
    functionalRoleCodes: ['director', 'storyboard_artist', 'visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无创建道具的权限',
  },
  'resource.propsEdit': {
    permissionCodes: ['resource.props_edit'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑道具的权限',
  },
  'resource.propsDelete': {
    permissionCodes: ['resource.props_delete'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除道具的权限',
  },
  'resource.propsUpload': {
    permissionCodes: ['resource.props_upload'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无上传道具素材的权限',
  },
  'resource.propsDownload': {
    permissionCodes: ['resource.props_download'],
    allowOwner: false,
    allowOrgAdmin: false,
    disabledReason: '当前账号暂无下载道具素材的权限',
  },
  'resource.propsTagCreate': {
    permissionCodes: ['resource.props_tag_create'],
    functionalRoleCodes: ['visual_designer', 'resource_producer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无创建道具标签的权限',
  },
  'resource.propsTagEdit': {
    permissionCodes: ['resource.props_tag_edit'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑道具标签的权限',
  },
  'resource.propsTagDelete': {
    permissionCodes: ['resource.props_tag_delete'],
    functionalRoleCodes: ['visual_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除道具标签的权限',
  },
  'resource.footageUpload': {
    permissionCodes: ['resource.footage_upload'],
    functionalRoleCodes: ['visual_designer', 'resource_producer', 'voice_actor', 'sound_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无上传文件的权限',
  },
  'resource.footageEdit': {
    permissionCodes: ['resource.footage_edit'],
    functionalRoleCodes: ['visual_designer', 'resource_producer', 'voice_actor', 'sound_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无编辑文件的权限',
  },
  'resource.footageDelete': {
    permissionCodes: ['resource.footage_delete'],
    functionalRoleCodes: ['visual_designer', 'resource_producer', 'voice_actor', 'sound_designer'],
    allowOwner: true,
    allowOrgAdmin: true,
    disabledReason: '当前账号暂无删除文件的权限',
  },
};

const PRODUCTION_ACTIONS: ReadonlySet<ProjectActionKey> = new Set([
  'project.setCover',
  'screenplay.upload',
  'screenplay.create',
  'screenplay.edit',
  'screenplay.parse',
  'storyboard.create',
  'storyboard.edit',
  'storyboard.delete',
  'storyboard.submitReview',
  'storyboard.approve',
  'asset.approve',
  'production.approveFinal',
  'task.assign',
  'screenplay.polish',
  'storyboard.textAssist',
  'storyboard.dialoguePolish',
  'storyboard.editDialogue',
  'storyboard.linkResource',
  'storyboard.plansCreate',
  'storyboard.plansEdit',
  'storyboard.plansDelete',
  'storyboard.generateImage',
  'storyboard.generateVideo',
  'storyboard.generateVoice',
  'storyboard.generateSoundEffect',
  'canvas.storyboardLayoutEdit',
  'canvas.assetLayoutEdit',
  'canvas.storyboardReferenceManage',
  'canvas.assetReferenceManage',
  'canvas.referenceUpload',
  'resource.createAny',
  'resource.deleteSubmitReview',
  'resource.deleteReviewApprove',
  'resource.promptAssist',
  'resource.generateImage',
  'resource.uploadAny',
  'resource.deleteAny',
  'resource.manageTags',
  'timeline.drag',
  'storyboard.dialogueCreate',
  'storyboard.dialogueEdit',
  'storyboard.dialogueDelete',
  'storyboard.imageUpload',
  'storyboard.imageDownload',
  'storyboard.imageDelete',
  'storyboard.videoUpload',
  'storyboard.videoDownload',
  'storyboard.videoDelete',
  'storyboard.voiceEdit',
  'storyboard.voiceDownload',
  'storyboard.voiceDelete',
  'storyboard.soundEffectEdit',
  'storyboard.soundEffectDownload',
  'storyboard.soundEffectDelete',
  'resource.charactersCreate',
  'resource.charactersEdit',
  'resource.charactersDelete',
  'resource.charactersUpload',
  'resource.charactersDownload',
  'resource.charactersTagCreate',
  'resource.charactersTagEdit',
  'resource.charactersTagDelete',
  'resource.scenesCreate',
  'resource.scenesEdit',
  'resource.scenesDelete',
  'resource.scenesUpload',
  'resource.scenesDownload',
  'resource.scenesTagCreate',
  'resource.scenesTagEdit',
  'resource.scenesTagDelete',
  'resource.propsCreate',
  'resource.propsEdit',
  'resource.propsDelete',
  'resource.propsUpload',
  'resource.propsDownload',
  'resource.propsTagCreate',
  'resource.propsTagEdit',
  'resource.propsTagDelete',
  'resource.footageUpload',
  'resource.footageEdit',
  'resource.footageDelete',
]);

const PURE_DOWNLOAD_ACTIONS: ReadonlySet<ProjectActionKey> = new Set([
  'storyboard.imageDownload',
  'storyboard.videoDownload',
  'storyboard.voiceDownload',
  'storyboard.soundEffectDownload',
  'resource.charactersDownload',
  'resource.scenesDownload',
  'resource.propsDownload',
]);

const PRODUCTION_WRITE_ACTIONS = PROJECT_ACTION_KEYS.filter(
  (action) => PRODUCTION_ACTIONS.has(action) && !PURE_DOWNLOAD_ACTIONS.has(action)
);

const PARSE_FLOW_ENTRY_ACTIONS: readonly ProjectActionKey[] = [
  'screenplay.upload',
  'screenplay.create',
  'screenplay.edit',
  'screenplay.parse',
  'storyboard.create',
  'storyboard.edit',
  'storyboard.delete',
  'storyboard.submitReview',
];

function hasAnyPermission(
  permissionCodes: readonly string[] | null | undefined,
  expected: Iterable<string>
): boolean {
  if (!permissionCodes || permissionCodes.length === 0) {
    return false;
  }
  const permissions = new Set(permissionCodes);
  for (const code of expected) {
    if (permissions.has(code)) {
      return true;
    }
  }
  return false;
}

function hasFunctionalRole(
  project: ProjectPermissionFacts['project'],
  expected: readonly FunctionalRoleCode[] | undefined
): boolean {
  if (!expected || expected.length === 0) return false;
  const expectedSet = new Set(expected);
  return (project?.functionalRoles ?? []).some((role) => expectedSet.has(role.code));
}

function hasAnyFunctionalRole(project: ProjectPermissionFacts['project']): boolean {
  return (project?.functionalRoles ?? []).length > 0;
}

function hasDirectorMemberManagement(project: ProjectPermissionFacts['project']): boolean {
  if (project?.canManageMembers === true) {
    return true;
  }
  return (project?.functionalRoles ?? []).some(
    (role) => role.code === 'director' && role.canManageMembers
  );
}

function hasParentScreenplayCreateGrant(project: ProjectPermissionFacts['project']): boolean {
  return project?.canCreateScreenplays === true;
}

function isScreenplayInitializationAction(action: ProjectActionKey): boolean {
  return action === 'screenplay.create' || action === 'screenplay.upload';
}

function isProjectActive(project: ProjectPermissionFacts['project']): boolean {
  return project?.status === 'active';
}

function isPersonalOwnedProject(project: ProjectPermissionFacts['project']): boolean {
  return (
    project?.orgId === null &&
    (project.type === 'mine' ||
      (project.type === 'project' && Number(project.folderCategory) === 1))
  );
}

function isProjectOwner(project: ProjectPermissionFacts['project'], currentUserId?: string | null) {
  return Boolean(
    (currentUserId && project?.ownerId === currentUserId) || isPersonalOwnedProject(project)
  );
}

function isProjectEditor(project: ProjectPermissionFacts['project']) {
  return project?.memberRole === 'owner' || project?.memberRole === 'editor';
}

function isProjectMemberOwner(project: ProjectPermissionFacts['project']) {
  return project?.memberRole === 'owner';
}

export function supportsProjectMemberManagement(
  project: ProjectPermissionFacts['project']
): boolean {
  return Boolean(project);
}

function isOrgAdmin(currentUserOrgRole?: number | null) {
  return currentUserOrgRole === 1 || currentUserOrgRole === 2;
}

function getForcedDisabledReason(facts: ProjectPermissionFacts, policy: ProjectActionPolicy) {
  if (!facts.project) return '未找到当前项目，无法执行该操作';
  if (facts.readonlyProjectShareToken) {
    return '当前处于项目分享只读模式，无法执行该操作';
  }
  if ((policy.requiresActiveProject ?? true) && !isProjectActive(facts.project)) {
    if (facts.project.status === 'paused') return '当前项目已暂停，只能查看，不能编辑';
    if (facts.project.status === 'archived') return '当前项目已归档，只能查看，不能编辑';
    return '当前项目不可编辑';
  }
  return null;
}

function evaluateProjectAction(
  action: ProjectActionKey,
  facts: ProjectPermissionFacts,
  policy: ProjectActionPolicy = PROJECT_ACTION_POLICY[action]
): boolean {
  const forcedDisabledReason = getForcedDisabledReason(facts, policy);
  if (forcedDisabledReason) return false;
  if (PURE_DOWNLOAD_ACTIONS.has(action)) {
    return Boolean(facts.project?.canDownload);
  }
  if (action === 'project.manageMembers' && !supportsProjectMemberManagement(facts.project)) {
    return false;
  }
  if (action === 'project.setCover') {
    if (policy.allowOwner && isProjectOwner(facts.project, facts.currentUserId)) return true;
    if (policy.allowOrgAdmin && isOrgAdmin(facts.currentUserOrgRole)) return true;
    if (hasAnyPermission(facts.orgPermissionCodes, PROJECT_COVER_ORG_PERMISSION_CODES)) return true;
  }
  const requiresFunctionalProductionRole =
    Boolean(facts.project?.orgId) && PRODUCTION_ACTIONS.has(action);

  if (
    requiresFunctionalProductionRole &&
    !isScreenplayInitializationAction(action) &&
    !isProjectOwner(facts.project, facts.currentUserId) &&
    !isProjectEditor(facts.project)
  ) {
    return false;
  }

  if (!requiresFunctionalProductionRole) {
    if (policy.allowOwner && isProjectOwner(facts.project, facts.currentUserId)) return true;
    if (policy.allowProjectMemberOwner && isProjectMemberOwner(facts.project)) return true;
    if (policy.allowProjectEditor && isProjectEditor(facts.project)) return true;
    if (policy.allowOrgAdmin && isOrgAdmin(facts.currentUserOrgRole)) return true;
    if (hasAnyPermission(facts.orgPermissionCodes, policy.permissionCodes ?? [])) return true;
  }
  if (action === 'project.manageMembers' && hasDirectorMemberManagement(facts.project)) return true;
  if (isScreenplayInitializationAction(action)) {
    const isOrgScopedProject = Boolean(facts.project?.orgId);
    if (policy.allowOrgAdmin && isOrgAdmin(facts.currentUserOrgRole)) {
      return true;
    }
    if (
      !isOrgScopedProject &&
      policy.allowOwner &&
      isProjectOwner(facts.project, facts.currentUserId)
    ) {
      return true;
    }
    if (
      !isOrgScopedProject &&
      policy.allowProjectMemberOwner &&
      isProjectMemberOwner(facts.project)
    ) {
      return true;
    }
  }
  if (hasFunctionalRole(facts.project, policy.functionalRoleCodes)) {
    return (
      !facts.project?.orgId ||
      isProjectOwner(facts.project, facts.currentUserId) ||
      isProjectEditor(facts.project)
    );
  }

  return false;
}

/** 仅图片编辑使用单对象权限；普通生图的聚合 action 保持不变。 */
export function canAiEditImage(
  access: ProjectEditorAccess | null | undefined,
  kind: string | null | undefined
): boolean {
  if (!access || access.readOnly || !access.facts.project) return false;
  if (kind === 'footage-file') return access.can('resource.footageEdit');
  if (kind === 'storyboard-image' || kind === 'markup-reference') {
    return access.can('storyboard.generateImage');
  }
  const permissions: Record<string, string> = {
    character: 'resource.characters_generate_image',
    scene: 'resource.scenes_generate_image',
    location: 'resource.scenes_generate_image',
    prop: 'resource.props_generate_image',
  };
  const permission = permissions[kind ?? ''];
  if (!permission) return false;
  return evaluateProjectAction('resource.generateImage', access.facts, {
    ...PROJECT_ACTION_POLICY['resource.generateImage'],
    permissionCodes: [permission],
  });
}

/** 图片超清入口权限：分镜走 image_upscale，资产对齐重绘，文件走 footageEdit，标注不开放。 */
export function canUpscaleImage(
  access: ProjectEditorAccess | null | undefined,
  kind: string | null | undefined
): boolean {
  if (!access || access.readOnly || !access.facts.project) return false;
  if (kind === 'markup-reference') return false;
  if (kind === 'storyboard-image') return access.can('storyboard.upscaleImage');
  if (kind === 'footage-file') return access.can('resource.footageEdit');
  const permissions: Record<string, string> = {
    character: 'resource.characters_generate_image',
    scene: 'resource.scenes_generate_image',
    location: 'resource.scenes_generate_image',
    prop: 'resource.props_generate_image',
  };
  const permission = permissions[kind ?? ''];
  if (!permission) return false;
  return evaluateProjectAction('resource.generateImage', access.facts, {
    ...PROJECT_ACTION_POLICY['resource.generateImage'],
    permissionCodes: [permission],
  });
}

export function canProjectAction(action: ProjectActionKey, facts: ProjectPermissionFacts): boolean {
  return evaluateProjectAction(action, facts);
}

export function getProjectActionDisabledReason(
  action: ProjectActionKey,
  facts: ProjectPermissionFacts
): string | null {
  if (canProjectAction(action, facts)) return null;
  const policy = PROJECT_ACTION_POLICY[action];
  if (action === 'project.manageMembers' && !supportsProjectMemberManagement(facts.project)) {
    return '当前账号暂无项目成员管理权限';
  }
  return getForcedDisabledReason(facts, policy) ?? policy.disabledReason;
}

export function isObserverOnlyProjectMember(
  project: ProjectPermissionInput | null | undefined
): boolean {
  const roleCodes = project?.functionalRoles?.map((role) => role.code) ?? [];
  return roleCodes.length === 1 && roleCodes[0] === 'observer';
}

export function canViewProjectDetails(project: ProjectPermissionInput | null | undefined): boolean {
  return !isObserverOnlyProjectMember(project);
}

export function canViewProjectHistory(project: ProjectPermissionInput | null | undefined): boolean {
  return !isObserverOnlyProjectMember(project);
}

function deriveProjectCanView(facts: ProjectPermissionFacts): boolean {
  const { project, currentUserId, currentUserOrgRole, orgPermissionCodes } = facts;
  if (!project) return false;
  return (
    isProjectOwner(project, currentUserId) ||
    Boolean(project.memberRole) ||
    isOrgAdmin(currentUserOrgRole) ||
    (orgPermissionCodes?.includes('project.view_all') ?? false)
  );
}

function deriveActions(facts: ProjectPermissionFacts): Record<ProjectActionKey, boolean> {
  return PROJECT_ACTION_KEYS.reduce(
    (result, action) => {
      result[action] = canProjectAction(action, facts);
      return result;
    },
    {} as Record<ProjectActionKey, boolean>
  );
}

function deriveCapabilities(
  facts: ProjectPermissionFacts,
  actions: Readonly<Record<ProjectActionKey, boolean>>
): ProjectCapabilities {
  const canView = deriveProjectCanView(facts);
  const canGenerate =
    actions['resource.generateImage'] ||
    actions['resource.promptAssist'] ||
    actions['storyboard.generateImage'] ||
    actions['storyboard.generateVideo'] ||
    actions['storyboard.upscaleVideo'] ||
    actions['storyboard.upscaleImage'] ||
    actions['storyboard.generateVoice'] ||
    actions['storyboard.generateSoundEffect'] ||
    actions['storyboard.textAssist'] ||
    actions['storyboard.dialoguePolish'] ||
    actions['screenplay.polish'];

  return {
    canView,
    canEdit:
      actions['screenplay.upload'] ||
      actions['screenplay.create'] ||
      actions['screenplay.edit'] ||
      actions['storyboard.editDialogue'] ||
      actions['storyboard.edit'] ||
      actions['storyboard.submitReview'] ||
      actions['storyboard.linkResource'] ||
      actions['storyboard.plansCreate'] ||
      actions['storyboard.plansEdit'] ||
      actions['storyboard.plansDelete'] ||
      actions['canvas.storyboardLayoutEdit'] ||
      actions['canvas.assetLayoutEdit'] ||
      actions['canvas.storyboardReferenceManage'] ||
      actions['canvas.assetReferenceManage'] ||
      actions['canvas.referenceUpload'] ||
      actions['resource.createAny'] ||
      actions['resource.uploadAny'] ||
      actions['resource.manageTags'] ||
      actions['storyboard.dialogueCreate'] ||
      actions['storyboard.dialogueEdit'] ||
      actions['storyboard.dialogueDelete'] ||
      actions['storyboard.imageUpload'] ||
      actions['storyboard.imageDelete'] ||
      actions['storyboard.videoUpload'] ||
      actions['storyboard.videoDelete'] ||
      actions['storyboard.trimVideo'] ||
      actions['storyboard.voiceEdit'] ||
      actions['storyboard.voiceDelete'] ||
      actions['storyboard.soundEffectEdit'] ||
      actions['storyboard.soundEffectDelete'] ||
      actions['resource.charactersCreate'] ||
      actions['resource.charactersEdit'] ||
      actions['resource.charactersDelete'] ||
      actions['resource.charactersUpload'] ||
      actions['resource.charactersTagCreate'] ||
      actions['resource.scenesCreate'] ||
      actions['resource.scenesEdit'] ||
      actions['resource.scenesDelete'] ||
      actions['resource.scenesUpload'] ||
      actions['resource.scenesTagCreate'] ||
      actions['resource.propsCreate'] ||
      actions['resource.propsEdit'] ||
      actions['resource.propsDelete'] ||
      actions['resource.propsUpload'] ||
      actions['resource.propsTagCreate'] ||
      actions['resource.footageUpload'] ||
      actions['resource.footageEdit'] ||
      actions['resource.footageDelete'],
    canManage: actions['project.manageMembers'],
    canDelete: actions['project.delete'],
    canShare: actions['project.share'],
    canExport: canView && actions['project.exportPackage'],
    canGenerate,
  };
}

export function deriveProjectEditorAccess(facts: ProjectPermissionFacts): ProjectEditorAccess {
  const actions = deriveActions(facts);
  const capabilities = deriveCapabilities(facts, actions);
  const hasProductionWriteAction = PRODUCTION_WRITE_ACTIONS.some((action) => actions[action]);
  const forcedReadOnlyReason = facts.readonlyProjectShareToken
    ? '当前处于项目分享只读模式，编辑类操作将被限制。'
    : facts.project?.status === 'paused'
      ? '当前项目已暂停，只能查看，不能编辑或发起新的生成任务。'
      : facts.project?.status === 'archived'
        ? '当前项目已归档，只能查看，不能编辑或发起新的生成任务。'
        : null;
  const hasActiveProductionHold =
    hasParentScreenplayCreateGrant(facts.project) || hasAnyFunctionalRole(facts.project);
  const readOnlyReason =
    forcedReadOnlyReason ??
    (!hasActiveProductionHold ? facts.project?.permissionRestrictionReason : null) ??
    (!hasProductionWriteAction && facts.project && !hasAnyFunctionalRole(facts.project)
      ? '当前账号暂无编辑该项目的权限，已切换为只读模式。'
      : null);

  return {
    facts,
    actions,
    capabilities,
    readOnly: Boolean(readOnlyReason),
    readOnlyReason,
    canAccessParseFlow: canAccessParseFlowProject(facts),
    can: (action) => actions[action],
    disabledReason: (action) => getProjectActionDisabledReason(action, facts),
  };
}

export function deriveProjectMenuAccess(facts: ProjectPermissionFacts): ProjectMenuAccess {
  const canEditSettings = canProjectAction('project.editSettings', facts);
  const canSetCover = canProjectAction('project.setCover', facts);
  const canManageMembers = canProjectAction('project.manageMembers', facts);
  const canShare = canProjectAction('project.share', facts);
  const canExport = deriveProjectCanView(facts) && canProjectAction('project.exportPackage', facts);
  const canArchive = canProjectAction('project.archive', facts);
  const canDelete = canProjectAction('project.delete', facts);

  return {
    canEditSettings,
    canSetCover,
    canManageMembers,
    canShare,
    canExport,
    canArchive,
    canDelete,
    hasAnyAction:
      canEditSettings ||
      canSetCover ||
      canManageMembers ||
      canShare ||
      canExport ||
      canArchive ||
      canDelete,
  };
}

export function deriveProjectCapabilities(
  project: Project | null | undefined,
  currentUserId: string | null | undefined,
  currentUserOrgRole: number | null | undefined,
  orgPermissionCodes: readonly string[] | null | undefined
): ProjectCapabilities {
  return deriveProjectEditorAccess({
    project,
    currentUserId,
    currentUserOrgRole,
    orgPermissionCodes,
  }).capabilities;
}

export function canUploadScreenplayToProject(
  project: ProjectPermissionShape | null | undefined,
  currentUserId: string | null | undefined
): boolean {
  return canProjectAction('screenplay.upload', { project, currentUserId });
}

export function canCreateScreenplayInProject(
  project: ProjectPermissionShape | null | undefined,
  currentUserId: string | null | undefined
): boolean {
  return canProjectAction('screenplay.create', { project, currentUserId });
}

export function canCreateStoryboardInProject(
  project: ProjectPermissionShape | null | undefined,
  currentUserId: string | null | undefined
): boolean {
  return canProjectAction('storyboard.create', { project, currentUserId });
}

export function canCreateSubprojectInProject(
  project: ProjectPermissionShape | null | undefined
): boolean {
  return project?.canCreateScreenplays === true && isProjectActive(project);
}

export function canAccessParseFlowProject(facts: ProjectPermissionFacts): boolean {
  if (hasParentScreenplayCreateGrant(facts.project) && isProjectActive(facts.project)) {
    return true;
  }
  return PARSE_FLOW_ENTRY_ACTIONS.some((action) => canProjectAction(action, facts));
}

export function canEditStoryboardInProject(
  project: ProjectPermissionShape | null | undefined,
  currentUserId: string | null | undefined
): boolean {
  return canProjectAction('storyboard.edit', { project, currentUserId });
}

export function shouldHideSubprojectScreenplayControl(): boolean {
  return true;
}

export function shouldWriteParentGrantsOnSubprojectSave(): boolean {
  return false;
}
